import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxI18nService } from '@studiohyperdrive/ngx-i18n';
import { combineLatest, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

import { BrowserService } from '@cjm/shared/core';
import { I18nKeys } from '@cjm/shared/ui/common';
import { ModalDialogService, ActionModalComponent } from '@cjm/shared/ui/modal';
import { environment } from 'environments';

import { AuthenticationService } from '../..';

@Injectable()
export class SessionErrorInterceptor implements HttpInterceptor {
	private readonly tokenError = 'refresh_token_exception';
	private readonly i18nKeys: typeof I18nKeys = I18nKeys;

	constructor(
		private readonly authService: AuthenticationService,
		private readonly i18nService: NgxI18nService,
		private readonly modalService: ModalDialogService,
		private readonly browserService: BrowserService
	) {}

	public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> | Observable<any> {
		// Iben: Check if the request is a call to our API, if not early exit
		if (!request.url.includes(environment.api.fullPath)) {
			return next.handle(request);
		}
		// Iben: If the error returns a 401, we check if we need to logout the user
		return next.handle(request).pipe(
			catchError((response) => {
				// Iben: If the errorCode matches the token, we logout the user
				if (response.status === 401 && response.error?.errorCode === this.tokenError) {
					// Iben: Logout and refresh
					return this.authService.logout().pipe(
						tap(() => {
							location.reload();
						})
					);
				}
				// add a check for the 419 status code
				if (response.status === 419) {
					// Abdurrahman: Show the login modal
					return this.showLoginModal();
				}

				// Iben: Return the error in regular cases
				return throwError(response);
			})
		);
	}

	/**
	 * showLoginModal
	 *
	 * The showLoginModal method will open the login modal to inform a user that
	 * their session has expired and they need to log in again.
	 *
	 */
	private showLoginModal(): Observable<boolean> {
		return combineLatest(
			this.i18nService.getTranslationObservable(this.i18nKeys.Modals.SessionExpired.Title),
			this.i18nService.getTranslationObservable(this.i18nKeys.Modals.SessionExpired.Message),
			this.i18nService.getTranslationObservable(this.i18nKeys.Modals.SessionExpired.Buttons.LogIn.Text),
			this.i18nService.getTranslationObservable(this.i18nKeys.Modals.SessionExpired.Buttons.LogIn.Title),
			this.i18nService.getTranslationObservable(this.i18nKeys.Modals.SessionExpired.Buttons.Cancel.Text),
			this.i18nService.getTranslationObservable(this.i18nKeys.Modals.SessionExpired.Buttons.Cancel.Title)
		).pipe(
			switchMap(([title, message, confirmText, confirmTitle, cancelText, cancelTitle]: string[]) => {
				// Abdurrahman: Create the confirmation modal.
				const modal = this.modalService.openModal(ActionModalComponent);
				const url = this.browserService.getCurrentUrl();

				modal.component.title = title;
				modal.component.text = message;
				modal.component.confirmButton = {
					text: confirmText,
					title: confirmTitle
				};
				modal.component.cancelButton = {
					text: cancelText,
					title: cancelTitle
				};

				return modal.component.buttonClicked.pipe(
					// Abdurrahman: Only take the first result in the stream
					take(1),
					// Abdurrahman: When the cancel button is clicked, reload the page and close the modal
					tap((value: 'confirm' | 'cancel') => {
						if (value === 'cancel') {
							this.modalService.instantCloseModal();
							location.reload();
						}
					}),
					// Abdurrahman: Only proceed if the action has been confirmed
					filter((value: 'confirm' | 'cancel') => value === 'confirm'),
					// Abdurrahman: Close the modal
					tap(() => this.modalService.closeModal()),
					switchMap(() =>
						// Redirect back to the page where the user was
						this.authService.switch({
							customCallBack: url
						})
					)
				);
			})
		);
	}
}
